import type { Celda, Columna, DocumentoExportable, FilaExportable, Seccion } from "./modelo"

/**
 * Una tabla PINTADA convertida en sección exportable (29/10).
 *
 * ⚠ **Se exporta lo que se ve, leído de la pantalla, y no una segunda consulta.** Con
 * ochenta tablas en el sistema, escribir para cada una «qué columnas y cómo se
 * formatean» sería ochenta copias de lo que ya dice su JSX, y la primera columna nueva
 * saldría en pantalla y no en el Excel. Leyendo el DOM, el archivo dice exactamente lo
 * que la persona tenía delante: los mismos filtros, el mismo período, la misma
 * búsqueda y las mismas filas cargadas.
 *
 * Qué se salta: la columna sin título visible (la de «Acciones», que sólo tiene un
 * `sr-only`), los campos, los iconos y lo marcado `data-exportar="no"`. El texto de un
 * BOTÓN sí sale: en el sistema el botón hace de enlace (el título de una tarea, el código
 * de una orden) y es el dato; los de acción viven en la columna sin título.
 * La fila de una sola celda a todo lo ancho es un aviso de «no hay nada» si es la única,
 * y un RÓTULO de grupo si hay más.
 *
 * ⚠ **Una columna es de cifras sólo si va alineada a la DERECHA.** Un código de cuenta
 * «3.3» o un expediente «2026143» parecen números y no lo son: convertidos, Excel los
 * sumaría y les quitaría el punto. Las cifras del sistema van a la derecha (regla 10);
 * los identificadores, no. Y si las celdas llevan MONEDAS distintas —«USD 10» y
 * «BS 300»— la columna se queda en texto: un número sin su moneda es un dato falso.
 *
 * `data-exportar-valor` en una celda manda sobre lo que se lee en ella.
 */

const SALTAR = "input, select, textarea, svg, .sr-only, [data-exportar='no'], [hidden]"
const BLOQUE = /^(DIV|P|LI|UL|OL|BR|H[1-6])$/

function textoDe(nodo: Node): string {
  const partes: string[] = []
  const recorrer = (n: Node) => {
    if (n.nodeType === 3) {
      partes.push(n.textContent ?? "")
      return
    }
    if (n.nodeType !== 1) return
    const el = n as Element
    if (el.matches(SALTAR)) return
    const bloque = BLOQUE.test(el.tagName) || el.classList.contains("block")
    // Dos insignias seguidas («Resma» «Reponer») no pueden salir pegadas: cada elemento
    // en línea lleva un espacio a cada lado, que el recorte final colapsa.
    const borde = bloque ? "\n" : " "
    partes.push(borde)
    for (const hijo of el.childNodes) recorrer(hijo)
    partes.push(borde)
  }
  recorrer(nodo)
  return partes
    .join("")
    .split("\n")
    .map((l) => l.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join(" · ")
}

/** Las únicas unidades que acompañan a una cifra: monedas, porcentaje y tamaños. */
const UNIDADES = new Set([
  "$",
  "€",
  "%",
  "USD",
  "USDT",
  "BS",
  "Bs",
  "Bs.",
  "VES",
  "EUR",
  "KB",
  "MB",
])
const NUMERO = /^([A-Za-z$€.]{0,5})\s*([-−+]?[\d.,]+)\s*([A-Za-z$€%.]{0,5})$/
const VENEZOLANO = /^\d{1,3}(?:\.\d{3})*(?:,\d+)?$|^\d+(?:,\d+)?$/
/** El decimal crudo del API —«1500.00», «36.500000»—: un punto con 1, 2 o ≥ 4 decimales. */
const CRUDO = /^\d+\.(?:\d{1,2}|\d{4,})$/
const FECHA = /^(\d{2})\/(\d{2})\/(\d{4})$/

/**
 * «USD 1.234,56» → { valor: 1234.56, unidad: "USD" }. `null` si no es una cifra.
 *
 * ⚠ **«1.500» es mil quinientos** (formato de aquí); un punto con tres decimales no se
 * puede distinguir, y gana el formato venezolano. Con 1, 2 o más de 3 decimales es el
 * decimal crudo del API. Y la unidad tiene que ser una de `UNIDADES`: «v3» o «5 d» no
 * son cifras con moneda.
 */
export function leerCifra(texto: string): { valor: number; unidad: string } | null {
  const m = NUMERO.exec(texto.replace(/ /g, " ").trim())
  if (!m) return null
  const unidad = (m[1] || m[3] || "").trim()
  if (unidad && !UNIDADES.has(unidad)) return null
  if (m[1] && m[3]) return null
  const crudo = (m[2] ?? "").replace("−", "-")
  const signo = crudo.startsWith("-") ? -1 : 1
  const cuerpo = crudo.replace(/^[-+]/, "")
  let valor: number
  if (CRUDO.test(cuerpo)) valor = Number(cuerpo)
  else if (VENEZOLANO.test(cuerpo)) valor = Number(cuerpo.replace(/\./g, "").replace(",", "."))
  else return null
  if (!Number.isFinite(valor)) return null
  return { valor: signo * valor, unidad }
}

/** «31/10/2026» → «2026-10-31». */
export function leerFecha(texto: string): string | null {
  const m = FECHA.exec(texto.trim())
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null
}

const vacio = (t: string) => t === "" || t === "—" || t === "-"

function alineadaDerecha(celda: Element | undefined): boolean {
  return celda?.classList.contains("text-right") ?? false
}

type Bruta = { textos: string[]; grupo: boolean; derecha: boolean[] }

/** Las filas de un `tbody`/`tfoot`, respetando `colSpan` y saltando las columnas fuera. */
function filasDe(filas: Element[], usar: boolean[], total: number): Bruta[] {
  const salida: Bruta[] = []
  for (const tr of filas) {
    const celdas = [...tr.children] as HTMLTableCellElement[]
    if (celdas.length === 1 && (celdas[0]?.colSpan ?? 1) > 1 && total > 1) {
      salida.push({ textos: [textoDe(celdas[0] as Node)], grupo: true, derecha: [false] })
      continue
    }
    const textos: string[] = []
    const derecha: boolean[] = []
    let columna = 0
    for (const c of celdas) {
      const ancho = c.colSpan || 1
      const valor = c.getAttribute("data-exportar-valor") ?? textoDe(c)
      for (let k = 0; k < ancho; k++) {
        if (usar[columna + k]) {
          textos.push(k === 0 ? valor : "")
          derecha.push(alineadaDerecha(c))
        }
      }
      columna += ancho
    }
    salida.push({ textos, grupo: false, derecha })
  }
  return salida
}

export function seccionDeTabla(tabla: HTMLTableElement, tituloPorOmision = "Tabla"): Seccion {
  const ths = [...tabla.querySelectorAll("thead tr:last-child th")] as HTMLTableCellElement[]
  const titulos: string[] = []
  const derechaTh: boolean[] = []
  for (const th of ths) {
    const texto = th.getAttribute("data-exportar") === "no" ? "" : textoDe(th)
    for (let k = 0; k < (th.colSpan || 1); k++) {
      titulos.push(k === 0 ? texto : "")
      derechaTh.push(alineadaDerecha(th))
    }
  }
  const usar = titulos.map((t) => t !== "")
  const indices = usar.flatMap((u, i) => (u ? [i] : []))

  const cuerpo = filasDe([...tabla.querySelectorAll("tbody > tr")], usar, titulos.length)
  const pie = filasDe([...tabla.querySelectorAll("tfoot > tr")], usar, titulos.length)
  // Una sola fila a lo ancho es el «no hay nada»: la sección sale vacía, no con un rótulo.
  const datos = cuerpo.length === 1 && cuerpo[0]?.grupo ? [] : cuerpo

  const columnas: Columna[] = []
  const convertir: ((t: string) => Celda)[] = []
  indices.forEach((indice, j) => {
    const textos = datos.filter((f) => !f.grupo).map((f) => f.textos[j] ?? "")
    const llenos = textos.filter((t) => !vacio(t))
    const derecha =
      derechaTh[indice] || datos.some((f) => !f.grupo && f.derecha[j] && !vacio(f.textos[j] ?? ""))
    const titulo = titulos[indice] ?? ""
    const cifras = llenos.map(leerCifra)
    const unidades = new Set(cifras.map((c) => c?.unidad))
    if (derecha && llenos.length > 0 && cifras.every((c) => c !== null) && unidades.size === 1) {
      const unidad = [...unidades][0] ?? ""
      const decimales = llenos.some((t) => t.includes(","))
      columnas.push({
        titulo: unidad ? `${titulo} (${unidad})` : titulo,
        tipo: decimales ? "monto" : "numero",
      })
      convertir.push((t) => (vacio(t) ? null : (leerCifra(t)?.valor ?? t)))
    } else if (llenos.length > 0 && llenos.every((t) => leerFecha(t) !== null)) {
      columnas.push({ titulo, tipo: "fecha" })
      convertir.push((t) => (vacio(t) ? null : leerFecha(t)))
    } else {
      columnas.push({ titulo, tipo: "texto" })
      convertir.push((t) => (vacio(t) ? null : t))
    }
  })

  const aFila = (f: Bruta): FilaExportable =>
    f.grupo
      ? { celdas: [f.textos[0] ?? "", ...indices.slice(1).map(() => null)], grupo: true }
      : {
          celdas: f.textos.map((t, j) => {
            const c = convertir[j]
            return c ? c(t) : t
          }),
        }

  const caption = tabla.querySelector("caption")?.textContent?.trim()
  return {
    titulo: tabla.getAttribute("data-exportar-titulo") ?? (caption || tituloPorOmision),
    columnas,
    filas: datos.map(aFila),
    pie: pie.length ? pie.map((f) => aFila(f).celdas) : undefined,
  }
}

/** Todas las tablas dentro de `raiz`, cada una su sección. */
export function documentoDe(
  raiz: HTMLElement,
  titulo: string,
  subtitulo?: string,
): DocumentoExportable {
  const tablas = [...raiz.querySelectorAll("table")].filter(
    (t) => t.closest("[data-exportar='no']") === null,
  )
  return {
    titulo,
    subtitulo,
    secciones: tablas.map((t, i) =>
      seccionDeTabla(t, tablas.length > 1 ? `Tabla ${i + 1}` : titulo),
    ),
  }
}
